/**
 * The feed's preferences as a small store, on the console (the workbench's feedPrefs.ts keeps them on the machine):
 * the person's own, kept by the platform (`POST /home/v1/feedback`), so every browser they use agrees. The value
 * comes with each Home answer (`prefs`); a press changes it here at once, with the workbench's own rules
 * (`shared/home/prefsModel.ts`), so the card answers before the platform does, then takes the platform's answer.
 * A press that fails puts the value back and says so. HomeFeed calls the same four functions as on the desktop.
 */
import type { HomeFeedPrefs } from '@aioe/shared';
import { useSyncExternalStore } from 'react';

import { routes } from '../../api/client.ts';
import { EMPTY_PREFS, type FeedItem, type FeedPrefs, hideItem, normalisePrefs, setPinned, showItem, toggleRating } from '../../sharedHome.ts';

let current: FeedPrefs = EMPTY_PREFS;
const listeners = new Set<() => void>();
const answered = new Set<(error?: string) => void>();

function set(next: FeedPrefs) {
  current = next;
  for (const l of [...listeners]) l();
}

/** The platform's copy, as a Home answer carries it. */
export function takeFeedPrefs(prefs: HomeFeedPrefs | undefined): void {
  set(prefs ? normalisePrefs({ ...prefs, pins: [], favourites: [] }) : EMPTY_PREFS);
}

export function getFeedPrefs(): FeedPrefs {
  return current;
}

export function useFeedPrefs(): FeedPrefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getFeedPrefs,
    getFeedPrefs,
  );
}

/** Told when the platform has answered a press (with its words when it refused), so Home reads itself again. */
export function onFeedbackAnswered(fn: (error?: string) => void): () => void {
  answered.add(fn);
  return () => answered.delete(fn);
}

function send(next: FeedPrefs, body: Parameters<typeof routes.homeFeedback>[0]) {
  const before = current;
  set(next);
  routes
    .homeFeedback(body)
    .then((a) => {
      takeFeedPrefs(a.prefs);
      for (const fn of [...answered]) fn();
    })
    .catch((e: unknown) => {
      set(before);
      for (const fn of [...answered]) fn(e instanceof Error ? e.message : 'That press did not reach the platform; try again.');
    });
}

/** Useful / not useful as a toggle: the same press again clears it. */
export function rateFeedKind(kind: FeedItem['kind'], verdict: 'useful' | 'not-useful'): void {
  const cardKind = kind;
  send(toggleRating(current, cardKind, verdict), { cardId: `${cardKind}:any`, action: verdict });
}

export function showFeedItem(id: string): void {
  send(showItem(current, id), { cardId: id, action: 'show' });
}

export function pinFeedItem(id: string, on: boolean): void {
  send(setPinned(current, id, on), { cardId: id, action: on ? 'pin' : 'unpin' });
}

export function hideFeedItem(item: Pick<FeedItem, 'id' | 'changeKey'>): void {
  send(hideItem(current, item.id, item.changeKey, Date.now()), { cardId: item.id, action: 'hide', changeKey: item.changeKey });
}
